import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from 'react-router-dom';
import {
  Edit,
  Clock,
  Calendar,
  Search,
  Plus,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  User,
  Send,
  Eye,
  Settings,
  X,
  Download,
  Mail,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
} from "lucide-react";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { toast } from "react-hot-toast";
import { formatDate } from "../../../utils/stringUtils";
import { regularizationReasonService } from "../../../api/crm/config/configService";
import WarningPopup from "../../../components/WarningPopup";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";


// Helper function to format time strings (HH:MM) or Date objects to 24-hour format
// This extracts time the same way as the employee view to ensure consistency
const formatTime = (timeValue) => {
  if (!timeValue) return "Not recorded";

  try {
    let hours, minutes;

    // If it's already a time string (HH:MM or HH:MM:SS), extract hours and minutes
    if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}/)) {
      [hours, minutes] = timeValue.split(':').map(Number);
    }
    // If it's a Date object or ISO string, extract time using toTimeString() method
    // This matches how the backend formats times and ensures consistency with employee view
    else {
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) {
        return "Not recorded";
      }
      // Use toTimeString() to get consistent time extraction (same as employee view)
      const timeString = date.toTimeString().split(' ')[0]; // Gets "HH:MM:SS"
      [hours, minutes] = timeString.split(':').map(Number);
    }

    // Format as 12-hour AM/PM format
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const formattedMinutes = String(minutes).padStart(2, '0');
    return `${displayHours}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    return "Not recorded";
  }
};

const AttendanceRegularization = ({ onBack, userRole = "hr_manager", initialMode = "list", id, onClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showSuccess = useCallback((message) => toast.success(message), []);
  const showError = useCallback((message) => toast.error(message), []);
  const showWarning = useCallback((message) => toast(message, { icon: '⚠️' }), []);
  const showInfo = useCallback((message) => toast(message, { icon: 'ℹ️' }), []);
  const [regularizationRequests, setRegularizationRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "all");
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || "");
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || "");
  const [searchMetadata, setSearchMetadata] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [showRequestForm, setShowRequestForm] = useState(initialMode === "create");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(initialMode === "view");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: "",
    checkIn: "",
    checkOut: "",
    reason: "",
    reasonId: "",
    description: "",
    employeeId: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // State for searchable employee dropdown
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  // Store selected employee data to persist even after search is cleared
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Export and Selection State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Confirmation state for Approve/Reject
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    requestId: null,
    status: null,
    employeeName: null,
    comments: ""
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearchTerm(searchTerm.trim()),
      searchTerm.trim() ? 300 : 0
    );
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch regularization reasons for the form
  const { data: reasonsData } = useQuery({
    queryKey: ["regularizationReasons", "dropdown"],
    queryFn: () => regularizationReasonService.getDropdown()
  });
  const dynamicReasons = reasonsData?.data || [];
  const hardcodedReasons = [
    "Forgot to punch",
    "Biometric scanner issue",
    "Client visit",
    "Official work",
    "System error"
  ];
  const displayReasons = dynamicReasons.length > 0 ? dynamicReasons.map(r => r.name) : hardcodedReasons;

  useEffect(() => {
    loadRegularizationRequests();
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows(new Set()); // Reset selection when filters change
  }, [searchTerm, filterStatus, itemsPerPage, filterStartDate, filterEndDate]);

  // Load data when pagination or filters change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadRegularizationRequests();
    }, searchTerm ? 300 : 0); // Debounce only for search, immediate for other filters

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, currentPage, itemsPerPage, filterStartDate, filterEndDate]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterStartDate, filterEndDate, currentPage, itemsPerPage, setSearchParams]);

  // Debounce employee search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay for employee search

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Handle deep-linking to a specific request
  useEffect(() => {
    if (id && initialMode === "view") {
      const fetchRequestById = async () => {
        setIsLoading(true);
        try {
          const url = `${process.env.REACT_APP_API_BASE_URL}/regularization/${id}`;
          const response = await authFetch(url, {
        credentials: 'include',
            headers: {
                          }
          });
          const result = await response.json();
          if (result.success && result.data) {
            setSelectedRequest(result.data);
            setShowDetails(true);
          } else {
            showError("Failed to load request details");
          }
        } catch (error) {
          showError("An error occurred while loading request details");
        } finally {
          setIsLoading(false);
        }
      };
      fetchRequestById();
    }
  }, [id, initialMode]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage]);

  // React Query: Search Employees (fetch when dropdown is open, load initial if search empty)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['regularization-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      const trimmedSearch = debouncedEmployeeSearch?.trim() || "";
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
                  }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          return data.data.employees || [];
        }
      }
      return [];
    },
    enabled: employeeSearchOpen, // Call when search dropdown is open
    staleTime: 0,
  });

  // React Query: Delete Regularization Request Mutation
  const deleteRequestMutation = useMutation({
    mutationFn: async (requestId) => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/regularization/${requestId}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
                    'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete regularization request');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regularization-requests"] });
      queryClient.invalidateQueries({ queryKey: ["regularization-stats"] }); // If stats exist
    },
  });

  // Get employees to display in dropdown (only searched results, no initial load)
  const dropdownEmployees = searchedEmployeesData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  // Close dropdown when form closes (but preserve selectedEmployeeData)
  useEffect(() => {
    if (!showRequestForm) {
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      // Don't clear selectedEmployeeData here - it should persist until form is reset
    }
  }, [showRequestForm]);

  const loadRegularizationRequests = async () => {
    setIsLoading(true);
    setIsSearching(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      if (filterStatus !== 'all') {
        queryParams.append('status', filterStatus);
      }

      if (filterStartDate) {
        queryParams.append('startDate', filterStartDate);
      }

      if (filterEndDate) {
        queryParams.append('endDate', filterEndDate);
      }

      // Add pagination
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());

      const url = `${process.env.REACT_APP_API_BASE_URL}/regularization?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        headers: {
                  }
      });
      const data = await response.json();

      if (data.success) {
        setRegularizationRequests(data.data.requests || []);
        setSearchMetadata(data.data.searchMetadata || null);

        // Update pagination state from API response
        if (data.data.pagination) {
          setPagination({
            currentPage: data.data.pagination.currentPage || currentPage,
            totalPages: data.data.pagination.totalPages || 1,
            totalItems: data.data.pagination.totalItems || 0,
            itemsPerPage: data.data.pagination.itemsPerPage || itemsPerPage,
            hasNextPage: data.data.pagination.hasNextPage ?? false,
            hasPrevPage: data.data.pagination.hasPrevPage ?? false,
            limit: data.data.pagination.limit || itemsPerPage
          });
        }
      } else {
        showError(`Failed to load regularization requests: ${data.message}`);
        setRegularizationRequests([]);
        setSearchMetadata(null);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: itemsPerPage,
          hasNextPage: false,
          hasPrevPage: false,
          limit: itemsPerPage
        });
      }
    } catch (error) {
      showError("Failed to load regularization requests. Please try again.");
      setRegularizationRequests([]);
      setSearchMetadata(null);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: itemsPerPage,
        hasNextPage: false,
        hasPrevPage: false,
        limit: itemsPerPage
      });
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };



  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getToggleIcon = (status) => {
    switch (status) {
      case "pending":
        return <CheckCircle className="h-5 w-5" />;
      case "approved":
        return <XCircle className="h-5 w-5" />;
      case "rejected":
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <Settings className="h-5 w-5" />;
    }
  };

  const getToggleText = (status) => {
    switch (status) {
      case "pending":
        return "Approve";
      case "approved":
        return "Reject";
      case "rejected":
        return "Approve";
      default:
        return "Toggle";
    }
  };

  const getToggleTooltip = (status) => {
    switch (status) {
      case "pending":
        return "Click to Approve";
      case "approved":
        return "Click to Reject";
      case "rejected":
        return "Click to Approve";
      default:
        return "Toggle Status";
    }
  };

  const getToggleColor = (status) => {
    switch (status) {
      case "pending":
        return "text-green-600 hover:text-green-800 hover:bg-green-100";
      case "approved":
        return "text-red-600 hover:text-red-800 hover:bg-red-100";
      case "rejected":
        return "text-green-600 hover:text-green-800 hover:bg-green-100";
      default:
        return "text-gray-600 hover:text-gray-800 hover:bg-gray-100";
    }
  };

  const triggerStatusChange = (request, status) => {
    setConfirmModal({
      isOpen: true,
      requestId: request._id || request.id,
      status: status,
      employeeName: request.employeeName || (request.employeeId?.personalInfo ? `${request.employeeId.personalInfo.firstName} ${request.employeeId.personalInfo.lastName}` : "Employee"),
      comments: ""
    });
  };

  const handleConfirmAction = async () => {
    if (confirmModal.requestId) {
      await handleStatusChange(confirmModal.requestId, confirmModal.status, confirmModal.comments);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const handleStatusChange = async (requestId, newStatus, comments = "") => {
    try {

      let response;
      if (newStatus === "approved") {
        response = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}/regularization/${requestId}/approve`, {
        credentials: 'include',
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
                          },
            body: JSON.stringify({
              comments: comments || "Approved by HR Manager",
            }),
          }
        );
      } else if (newStatus === "rejected") {
        response = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}/regularization/${requestId}/reject`, {
        credentials: 'include',
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
                          },
            body: JSON.stringify({
              rejectionReason: comments || "Rejected by HR Manager",
              comments: comments || "Rejected by HR Manager",
            }),
          }
        );
      } else if (newStatus === "pending") {
        response = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}/regularization/${requestId}/pending`, {
        credentials: 'include',
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
                          },
            body: JSON.stringify({
              comments: comments || "Set to pending by HR Manager",
            }),
          }
        );
      }

      const data = await response.json();

      if (data.success) {
        showSuccess(`Request ${newStatus} successfully!`);
        await loadRegularizationRequests();
      } else {
        // Handle specific error messages with better UX
        const errorMessage = data.message || data.error || "";
        const errorText = errorMessage.toLowerCase();

        if (errorText.includes("already approved")) {
          showWarning("This request is already approved. No action needed.");
        } else if (errorText.includes("already rejected")) {
          showWarning("This request is already rejected. No action needed.");
        } else if (errorText.includes("already pending")) {
          showWarning("This request is already pending. No action needed.");
        } else {
          showError(errorMessage || `Unable to ${newStatus} request. Please try again.`);
        }
      }
    } catch (error) {
      showError(`Failed to ${newStatus} request. Please try again.`);
    }
  };

  const handleToggleApproval = async (request) => {
    const requestId = request._id || request.id;
    const currentStatus = request.status;

    if (currentStatus === "pending") {
      // If pending, approve it
      await handleStatusChange(requestId, "approved");
    } else if (currentStatus === "approved") {
      // If approved, reject it
      await handleStatusChange(requestId, "rejected");
    } else if (currentStatus === "rejected") {
      // If rejected, approve it
      await handleStatusChange(requestId, "approved");
    }
  };

  const validateDescription = (description) => {
    if (!description || description.trim().length < 10) {
      return "Description must be at least 10 characters";
    }
    if (description.length > 500) {
      return "Description must be no more than 500 characters";
    }
    if (!/^[a-zA-Z0-9\s,]+$/.test(description)) {
      return "Description can only contain alphanumeric characters, spaces, and commas";
    }
    return null;
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleFieldChange = (name, value) => {
    if (name === 'reason') {
      const reasonObj = dynamicReasons.find(r => (r._id || r.id) === value);
      if (reasonObj) {
        setFormData(prev => ({ ...prev, reason: reasonObj.name, reasonId: value }));
      } else {
        setFormData(prev => ({ ...prev, reason: value, reasonId: "" }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Field-level validation
    let error = "";
    if (name === 'employeeId' && !value) error = "Employee selection is required";
    if (name === 'date' && !value) error = "Date is required";
    if (name === 'reason' && !value) error = "Reason is required";
    if (name === 'checkIn' && !value) error = "Check-in time is required";
    if (name === 'checkOut' && !value) error = "Check-out time is required";

    if (name === 'description') {
      error = validateDescription(value);
    }

    // Time cross-validation
    if (name === 'checkIn' || name === 'checkOut') {
      const otherField = name === 'checkIn' ? 'checkOut' : 'checkIn';
      const otherValue = formData[otherField];
      if (value && otherValue) {
        const checkInVal = name === 'checkIn' ? value : otherValue;
        const checkOutVal = name === 'checkIn' ? otherValue : value;
        if (checkOutVal <= checkInVal) {
          setFormErrors(prev => ({ ...prev, timeValidation: "Check-out must be after check-in", [name]: "" }));
        } else {
          setFormErrors(prev => ({ ...prev, timeValidation: "", [name]: "" }));
        }
      }
    }

    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get sorted data
  const getSortedRequests = (requests) => {
    if (!sortField || !requests || requests.length === 0) {
      return requests;
    }

    const sorted = [...requests].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employee':
          aValue = (a.employeeName ||
            (a.employeeId?.personalInfo
              ? `${a.employeeId.personalInfo.firstName || ""} ${a.employeeId.personalInfo.lastName || ""}`.trim()
              : "Unknown Employee")).toLowerCase();
          bValue = (b.employeeName ||
            (b.employeeId?.personalInfo
              ? `${b.employeeId.personalInfo.firstName || ""} ${b.employeeId.personalInfo.lastName || ""}`.trim()
              : "Unknown Employee")).toLowerCase();
          break;

        case 'regularizationDate':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;

        case 'requestDate':
          aValue = new Date(a.submittedDate || a.createdAt).getTime();
          bValue = new Date(b.submittedDate || b.createdAt).getTime();
          break;

        case 'requestedTime':
          // Sort by check-in time
          aValue = a.requestedCheckIn || "";
          bValue = b.requestedCheckIn || "";
          break;

        case 'reason':
          aValue = (a.reason || "").toLowerCase();
          bValue = (b.reason || "").toLowerCase();
          break;

        case 'status':
          aValue = (a.status || "").toLowerCase();
          bValue = (b.status || "").toLowerCase();
          break;

        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare values
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedRegularizationRequests = useMemo(
    () => getSortedRequests(regularizationRequests),
    [regularizationRequests, sortField, sortOrder]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'description') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      if (processedValue.length > 500) processedValue = processedValue.slice(0, 500);
    }

    handleFieldChange(name, processedValue);
  };

  // --- Export and Selection Handlers ---

  const handleSelectAll = (e) => {
    const currentPageIds = regularizationRequests.map(req => req._id || req.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleDeleteRequest = (request) => {
    setRequestToDelete(request);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;
        let errorMessages = [];

        for (const id of ids) {
          try {
            await deleteRequestMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
            errorMessages.push(error.message);
          }
        }

        if (successCount > 0) {
          showSuccess(`${successCount} request(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          // Show specific error message if available, otherwise generic message
          const errorMessage = errorMessages.length > 0
            ? errorMessages[0]
            : `${failCount} request(s) could not be deleted.`;
          showError(errorMessage);
        }
      } else {
        await deleteRequestMutation.mutateAsync(requestToDelete._id || requestToDelete.id);
        showSuccess('Regularization request deleted successfully.');
      }
      setShowDeleteModal(false);
      setRequestToDelete(null);
      setIsBulkDelete(false);
      await loadRegularizationRequests();
    } catch (error) {
      if (!isBulkDelete) {
        // Show specific error message from API if available
        const errorMessage = error.message || 'Failed to delete regularization request. Please try again.';
        showError(errorMessage);
      }
    }
  };

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = debouncedSearchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== "all") f.status = filterStatus;
    if (filterStartDate) f.startDate = filterStartDate;
    if (filterEndDate) f.endDate = filterEndDate;
    return f;
  }, [debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate]);

  const currentPageRequestIds = useMemo(
    () =>
      sortedRegularizationRequests
        .map((req) => req._id || req.id)
        .filter(Boolean),
    [sortedRegularizationRequests]
  );

  const exportSortBy = useMemo(() => {
    if (sortField === "employee") return "employee";
    if (sortField === "regularizationDate") return "regularizationDate";
    if (sortField === "requestDate") return "requestDate";
    if (sortField === "requestedTime") return "requestedTime";
    if (sortField === "reason") return "reason";
    if (sortField === "status") return "status";
    return "submittedDate";
  }, [sortField]);

  const exportSortOrder = useMemo(() => {
    if (sortField === null) return "desc";
    return sortOrder;
  }, [sortField, sortOrder]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      currentPageIds,
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const payload = {
        module: "regularization",
        format,
        scope,
        filters: overrideFilters ?? baseExportFilters,
        page: overridePage ?? currentPage,
        limit: overrideLimit ?? itemsPerPage,
        sortBy: exportSortBy,
        sortOrder: exportSortOrder,
        selectedIds,
      };
      if (Array.isArray(currentPageIds) && currentPageIds.length > 0) {
        payload.currentPageIds = currentPageIds;
      }
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: "POST",
          headers: {
            "Content-Type": "application/json",
                      },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || "Export request failed";
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage) msg = j.responseMessage;
          else if (j?.error && typeof j.error === "string") msg = j.error;
        } catch {
          /* keep msg */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === "xlsx" ? "xlsx" : format;
      const fallbackName = `regularization-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      exportSortBy,
      exportSortOrder,
    ]
  );

  const handleRegularizationSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        showError("No selected requests to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        showSuccess(`${ids.length} request(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        showError(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, showSuccess, showError]
  );

  const handleRegularizationScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === "currentPage" && !sortedRegularizationRequests.length) {
          showError("No requests to export on the current page.");
          return;
        }

        const totalFiltered = Number(pagination?.totalItems ?? 0);
        if (scope === "filtered" && totalFiltered === 0) {
          showError("No requests match the current filters.");
          return;
        }

        if (scope === "dateRange") {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            showError("Please select start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === "all") {
          const trimmed = debouncedSearchTerm?.trim();
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {
              ...(trimmed ? { search: trimmed } : {}),
              ...(filterStatus !== "all" ? { status: filterStatus } : {}),
            },
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
            currentPageIds: currentPageRequestIds,
          });
        } else {
          return;
        }

        showSuccess("Export downloaded.");
        setShowExportModal(false);
      } catch (error) {
        showError(error?.message || "Export failed. Please try again.");
      } finally {
        setExportLoading(false);
      }
    },
    [
      sortedRegularizationRequests.length,
      pagination?.totalItems,
      baseExportFilters,
      debouncedSearchTerm,
      filterStatus,
      requestBackendExport,
      currentPageRequestIds,
      showSuccess,
      showError,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();

    // Final validation before submission
    const errors = {};
    if (!formData.employeeId) errors.employeeId = "Employee selection is required";
    if (!formData.date) errors.date = "Date is required";
    if (!formData.reason) errors.reason = "Reason is required";
    if (!formData.checkIn) errors.checkIn = "Check-in time is required";
    if (!formData.checkOut) errors.checkOut = "Check-out time is required";

    const descriptionError = validateDescription(formData.description);
    if (descriptionError) errors.description = descriptionError;

    if (formData.checkIn && formData.checkOut && formData.checkOut <= formData.checkIn) {
      errors.timeValidation = "Check-out must be after check-in";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError("Please fix the errors in the form before submitting.");
      return;
    }

    setFormErrors({});

    try {

      // Validate employee selection
      if (!formData.employeeId) {
        showError("❌ Please select an employee");
        return;
      }

      const requestData = {
        employeeId: formData.employeeId, // Use MongoDB _id from selectedEmployeeData
        date: formData.date,
        requestedCheckIn: formData.checkIn,
        requestedCheckOut: formData.checkOut,
        reason: formData.reason,
        reasonId: formData.reasonId || null,
        description: formData.description,
        requestType: "both",
        priority: "medium",
      };
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization`, {
        credentials: 'include',
          method: "POST",
          headers: {
            "Content-Type": "application/json",
                      },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess("Regularization request submitted successfully!");
        setFormData({
          date: "",
          checkIn: "",
          checkOut: "",
          reason: "",
          reasonId: "",
          description: "",
          employeeId: "",
        });
        setFormErrors({});
        setSelectedEmployeeData(null);
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
        setShowRequestForm(false);
        if (onClose) onClose();
        // Reload requests to show the new one
        await loadRegularizationRequests();
      } else {
        // Handle specific validation errors
        const errorMessage = data.message || data.error || "";
        const errorText = errorMessage.toLowerCase();

        if (errorText.includes("already exists") || errorText.includes("regularization request already exists")) {
          showWarning("A regularization request already exists for this date. Please select a different date or check existing requests.");
        } else if (errorText.includes("workinghours")) {
          showError("Invalid time range: Check-out time must be after check-in time. Please correct the times.");
        } else if (errorText.includes("validation failed")) {
          showError("Validation failed: Please check all required fields and ensure times are valid.");
        } else {
          showError(errorMessage || "Unable to submit request. Please try again.");
        }
      }
    } catch (error) {
      showError("Failed to submit regularization request. Please try again.");
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  if (showDetails && selectedRequest) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowDetails(false);
                if (onClose) onClose();
              }}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              Regularization Request Details
            </h2>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employee Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Employee Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <User className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium break-words capitalize block">
                      {selectedRequest.employeeName ||
                        (selectedRequest.employeeId?.personalInfo
                          ? `${selectedRequest.employeeId.personalInfo.firstName ||
                            ""
                            } ${selectedRequest.employeeId.personalInfo.lastName ||
                            ""
                            }`.trim()
                          : "Unknown Employee")}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <FileText className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Employee ID:</span>
                    <span className="text-sm font-medium break-words block">
                      {selectedRequest.employeeOfficialId || "No ID"}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Mail className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="text-sm font-medium break-words block">
                      {selectedRequest.officialEmail || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Request Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="text-sm font-medium">
                    {formatDate(selectedRequest.date, { includeTime: false })}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(selectedRequest.status)}
                  <span className="text-sm text-gray-600">Status:</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                      selectedRequest.status
                    )}`}
                  >
                    {selectedRequest.status.charAt(0).toUpperCase() +
                      selectedRequest.status.slice(1)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Submitted:</span>
                  <span className="text-sm font-medium">
                    {formatDate(selectedRequest.createdAt || selectedRequest.submittedDate, { includeTime: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Time Details */}
          <div className="mt-6 bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Requested Time Details
            </h3>
            <div className="space-y-1">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Check In:</span> {formatTime(selectedRequest.requestedCheckIn)}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Check Out:</span> {formatTime(selectedRequest.requestedCheckOut)}
              </div>
            </div>
          </div>

          {/* Reason and Description */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Reason</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedRequest.reason}
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Description
            </h3>
            <p className="text-sm text-gray-600 break-words whitespace-normal first-letter:capitalize">
              {selectedRequest.description}
            </p>
          </div>

          {/* Approval Information */}
          {selectedRequest.status !== "pending" && (
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Additional Information
              </h3>
              <div className="space-y-3">
                {selectedRequest.approvedDate && (
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {selectedRequest.status === "approved" ? "Approved On:" : "Rejected On:"}
                    </span>
                    <span className="text-sm font-medium">
                      {formatDate(selectedRequest.approvedDate, { includeTime: true })}
                    </span>
                  </div>
                )}
                {(selectedRequest.approvedByName || selectedRequest.approvedBy) && (
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{selectedRequest.status === "approved" ? "Approved By:" : "Action By:"}</span>
                    <span className="text-sm font-medium">
                      {selectedRequest.approvedByName ||
                        (selectedRequest.approvedBy?.firstName ? `${selectedRequest.approvedBy.firstName} ${selectedRequest.approvedBy.lastName}` : "Approver")}
                    </span>
                  </div>
                )}
                {selectedRequest.status === "rejected" && selectedRequest.rejectionReason && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600 font-medium">Rejection Reason:</span>
                    <p className="mt-1 text-red-600 bg-red-50 p-2 rounded border border-red-100 whitespace-pre-wrap first-letter:capitalize">
                      {selectedRequest.rejectionReason}
                    </p>
                  </div>
                )}
                {selectedRequest.comments && selectedRequest.comments !== selectedRequest.rejectionReason && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600 font-medium">Approver Comments:</span>
                    <p className="mt-1 text-gray-800 bg-white p-2 rounded border border-gray-200 whitespace-pre-wrap first-letter:capitalize">
                      {selectedRequest.comments}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showRequestForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRequestForm(false)}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              New Regularization Request
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmitRequest} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Employee <span className="text-red-500">*</span>
              </label>
              <div className="relative employee-dropdown-container">
                {/* Display selected employee or placeholder */}
                <div
                  onClick={() => {
                    setEmployeeSearchOpen(!employeeSearchOpen);
                    if (!employeeSearchOpen) {
                      setEmployeeSearchTerm("");
                    }
                  }}
                  className={`mt-1 w-full border rounded-md px-3 py-2 bg-white cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 ${formErrors.employeeId ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                >
                  <span className={formData.employeeId ? "text-gray-900" : "text-gray-500"}>
                    {formData.employeeId && selectedEmployeeData
                      ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""
                      } (${selectedEmployeeData.employeeId || ""})`
                      : "Select Employee"}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${employeeSearchOpen ? "transform rotate-180" : ""
                      }`}
                  />
                </div>

                {/* Dropdown with search */}
                {employeeSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by employee name..."
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setEmployeeSearchTerm(value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {isLoadingEmployeeSearch ? (
                        <div className="p-4 text-center text-gray-500 text-sm italic flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                          <span>Loading employees...</span>
                        </div>
                      ) : dropdownEmployees.length > 0 ? (
                        dropdownEmployees.map((employee) => (
                          <div
                            key={employee._id}
                            onClick={() => {
                              handleFieldChange('employeeId', employee._id);
                              setSelectedEmployeeData(employee);
                              setEmployeeSearchOpen(false);
                              setEmployeeSearchTerm("");
                            }}
                            className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.employeeId === employee._id ? "bg-blue-50" : ""}`}
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {employee.personalInfo?.firstName || ""} {employee.personalInfo?.lastName || ""}
                            </div>
                            <div className="text-xs text-gray-500">
                              {employee.employeeId || ""} - {employee.employmentInfo?.department || ""}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {debouncedEmployeeSearch.trim() ? "No employees found" : "Type to search employees..."}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {formErrors.employeeId && (
                <p className="mt-1 text-xs text-red-500">{formErrors.employeeId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                required
                max={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.date ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {formErrors.date && (
                <p className="mt-1 text-xs text-red-500">{formErrors.date}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                name="reason"
                value={formData.reasonId || formData.reason}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.reason ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              >
                <option value="">Select reason</option>
                {dynamicReasons.length > 0 ? (
                  dynamicReasons.map((r) => (
                    <option key={r._id || r.id} value={r._id || r.id}>
                      {r.name}
                    </option>
                  ))
                ) : (
                  hardcodedReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))
                )}
              </select>
              {formErrors.reason && (
                <p className="mt-1 text-xs text-red-500">{formErrors.reason}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check In Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                name="checkIn"
                value={formData.checkIn}
                onChange={handleInputChange}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.checkIn || formErrors.timeValidation ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {formErrors.checkIn && (
                <p className="mt-1 text-xs text-red-500">{formErrors.checkIn}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check Out Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                name="checkOut"
                value={formData.checkOut}
                onChange={handleInputChange}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.checkOut || formErrors.timeValidation ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {formErrors.checkOut && (
                <p className="mt-1 text-xs text-red-500">{formErrors.checkOut}</p>
              )}
              {formErrors.timeValidation && (
                <p className="mt-1 text-xs text-red-500">{formErrors.timeValidation}</p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(10-500 characters)</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              onKeyPress={handleTextKeyPress}
              required
              rows={4}
              maxLength={500}
              placeholder="Please provide detailed explanation..."
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            />
            <div className="flex justify-between items-center mt-1">
              {formErrors.description ? (
                <p className="text-sm text-red-600">{formErrors.description}</p>
              ) : (
                <div></div>
              )}
              <span className="text-xs text-gray-500">
                {formData.description.length}/500
              </span>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowRequestForm(false);
                setSelectedEmployeeData(null);
                setEmployeeSearchOpen(false);
                setEmployeeSearchTerm("");
                if (onClose) onClose();
              }}
              aria-label="Cancel regularisation request"
            >
              Cancel request
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex items-center space-x-2"
              aria-label="Submit regularisation request"
            >
              <Send className="h-4 w-4" />
              <span>Submit regularisation request</span>
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Attendance Regularization
            </h2>
          </div>
          <div className="flex space-x-2">
            {selectedRows.size > 0 && (
              <PermissionGate permission="attendance" action="delete">
                <Button
                  variant="primary"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="attendance" action="read">
              <div className="relative shrink-0 mr-2" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  size="sm"
                  icon={
                    exportLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )
                  }
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={`flex items-center space-x-2 ${selectedRows.size > 0 ? "bg-blue-50 border-blue-200 text-blue-600" : ""}`}
                >
                  <span>
                    {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
                  </span>
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleRegularizationSelectedFormatExport}
                />
              </div>
            </PermissionGate>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowRequestForm(true)}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>New Request</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Employee Name, Employee ID or Email ID..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  // Maintain focus on search input
                  const searchInput = document.querySelector('input[placeholder="Search requests..."]');
                  if (searchInput) {
                    setTimeout(() => searchInput.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Date Filter Range */}
          <div className="flex items-center gap-2">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={(range) => {
                setFilterStartDate(range.startDate);
                setFilterEndDate(range.endDate);
              }}
            />

            {/* Clear Filters */}
            {(searchTerm || filterStatus !== "all" || filterStartDate || filterEndDate) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("all");
                  setFilterStartDate("");
                  setFilterEndDate("");
                }}
                className="h-[44px] border-gray-200 text-gray-600 hover:bg-gray-50 transition-all rounded-lg shadow-sm px-4 py-0 text-sm font-semibold"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left w-12 bg-gray-50">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                  checked={regularizationRequests.length > 0 && selectedRows.size === regularizationRequests.length}
                  onChange={handleSelectAll}
                />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 select-none bg-gray-50"
                onClick={() => handleSort('employee')}
              >
                <div className="flex items-center space-x-1">
                  <span>Employee</span>
                  {sortField === 'employee' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-blue-600" /> :
                      <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none bg-gray-50"
                onClick={() => handleSort('regularizationDate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Regularization Date</span>
                  {sortField === 'regularizationDate' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-blue-600" /> :
                      <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 select-none bg-gray-50"
                onClick={() => handleSort('requestedTime')}
              >
                <div className="flex items-center space-x-1">
                  <span>Requested Time</span>
                  {sortField === 'requestedTime' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-blue-600" /> :
                      <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] cursor-pointer hover:bg-gray-100 select-none bg-gray-50"
                onClick={() => handleSort('reason')}
              >
                <div className="flex items-center space-x-1">
                  <span>Reason</span>
                  {sortField === 'reason' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-blue-600" /> :
                      <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none bg-gray-50"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  {sortField === 'status' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-blue-600" /> :
                      <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 select-none bg-gray-50"
                onClick={() => handleSort('requestDate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Request Date</span>
                  {sortField === 'requestDate' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-blue-600" /> :
                      <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[130px] bg-gray-50">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading || isSearching ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-gray-500">
                      {isSearching ? "Searching..." : "Loading regularization requests..."}
                    </span>
                  </div>
                </td>
              </tr>
            ) : regularizationRequests.length > 0 ? (
              sortedRegularizationRequests.map((request) => (
                <tr
                  key={request._id || request.id}
                  className={`hover:bg-gray-50 ${selectedRows.has(request._id || request.id) ? 'bg-blue-50/50' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      checked={selectedRows.has(request._id || request.id)}
                      onChange={() => handleSelectRow(request._id || request.id)}
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap overflow-hidden">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate capitalize">
                        {request.employeeName ||
                          (request.employeeId?.personalInfo
                            ? `${request.employeeId.personalInfo.firstName || ""
                              } ${request.employeeId.personalInfo.lastName || ""
                              }`.trim()
                            : "Unknown Employee")}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {request.employeeId?.employeeId || "No ID"}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {request.employeeId?.user?.email || "Unknown Email"}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(request.date, { includeTime: false })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="min-w-0">
                      <div>In: {formatTime(request.requestedCheckIn)}</div>
                      <div>Out: {formatTime(request.requestedCheckOut)}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="truncate max-w-[170px] capitalize" title={request.reason}>
                      {request.reason}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(request.status)}
                      <span
                        className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {request.status.charAt(0).toUpperCase() +
                          request.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(request.createdAt || request.submittedDate, { includeTime: true })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                        className="text-blue-600 hover:text-blue-900"
                        aria-label="View regularisation request details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {request.status === "pending" && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => triggerStatusChange(request, "approved")}
                            className="text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1"
                            title="Approve this request"
                            aria-label="Approve regularisation request"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => triggerStatusChange(request, "rejected")}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1"
                            title="Reject this request"
                            aria-label="Reject regularisation request"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {request.status === "approved" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => triggerStatusChange(request, "rejected")}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1"
                          title="Reject this request"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                      {request.status === "rejected" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => triggerStatusChange(request, "approved")}
                          className="text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1"
                          title="Approve this request"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <PermissionGate permission="attendance" action="delete">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRequest(request)}
                          className="text-red-500 hover:text-red-800"
                          aria-label="Delete regularization request"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="bg-white hover:bg-white">
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500 italic">
                  No regularization requests found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(() => {
        const regularizationTotalItems = Number(pagination?.totalItems || regularizationRequests.length || 0);
        const regularizationTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || pagination?.itemsPerPage || itemsPerPage || 10;
        const currentPageNum = pagination?.currentPage || currentPage || 1;
        const shouldShowPagination = !isLoading && !isSearching && regularizationTotalItems > effectivePaginationLimit;

        const handleRegularizationPageChange = (page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        return shouldShowPagination ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <PaginationControls
              currentPage={currentPageNum}
              totalPages={regularizationTotalPages}
              totalItems={regularizationTotalItems}
              limit={effectivePaginationLimit}
              hasNextPage={currentPageNum < regularizationTotalPages}
              hasPrevPage={currentPageNum > 1}
              onPageChange={handleRegularizationPageChange}
              onLimitChange={handleItemsPerPageChange}
            />
          </div>
        ) : null;
      })()}

      {
        !isLoading && !isSearching && regularizationRequests.length === 0 && (
          <div className="text-center py-12">
            <Edit className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchMetadata && searchMetadata.appliedFilters > 0
                ? "No regularization requests found matching your search criteria"
                : "No regularization requests found"
              }
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchMetadata && searchMetadata.appliedFilters > 0
                ? "Try adjusting your search criteria or clear filters to see all requests."
                : "Create a new regularization request to get started."
              }
            </p>
          </div>
        )
      }


      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Regularization Requests"
        showDateRange
        scopedFormatExport={{
          onExport: handleRegularizationScopedExport,
          formats: ["csv", "xlsx", "pdf"],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Confirmation Modal */}
      {
        confirmModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] !mt-0 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className={`p-1 h-2 ${confirmModal.status === 'approved' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-full mr-3 ${confirmModal.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {confirmModal.status === 'approved' ? <CheckCircle className="h-6 w-6" /> : <AlertCircle className="h-6 w-6" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {confirmModal.status === 'approved' ? 'Approve' : 'Reject'} Request
                  </h3>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">
                    Are you sure you want to <strong>{confirmModal.status}</strong> the regularization request for <strong>{confirmModal.employeeName}</strong>?
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comments {confirmModal.status === 'rejected' ? '*' : '(Optional)'}
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none h-24"
                      placeholder={`Enter ${confirmModal.status === 'rejected' ? 'rejection reason' : 'comments'}...`}
                      value={confirmModal.comments}
                      onChange={(e) => setConfirmModal({ ...confirmModal, comments: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmAction}
                    className={confirmModal.status === 'approved' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                    disabled={confirmModal.status === 'rejected' && !confirmModal.comments.trim()}
                  >
                    Confirm {confirmModal.status === 'approved' ? 'Approval' : 'Rejection'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRequestToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteRequest}
        title={isBulkDelete ? "Delete Regularization Requests" : "Delete Regularization Request"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected regularization requests? This action cannot be undone.`
            : "Are you sure you want to delete this regularization request? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div >
  );
};

export default AttendanceRegularization;
